import { printoutPageSettingsSchema, printoutTemplateBlocksSchema } from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { paginated, paginationInputSchema } from './common/pagination.js';

const guarded = { ...commonErrors };
export const PRINTOUT_TEMPLATE_MEDIA_MAX_BYTES = 5 * 1024 * 1024;
export const printoutTemplatePreviewInputSchema = z.object({
  organizationId: z.uuid(),
  blocks: printoutTemplateBlocksSchema,
  settings: printoutPageSettingsSchema,
});
export type PrintoutTemplatePreviewInput = z.infer<typeof printoutTemplatePreviewInputSchema>;
export const printoutTemplateMediaSchema = z.object({
  id: z.uuid(),
  fileName: z.string(),
  contentType: z.enum(['image/png', 'image/jpeg']),
  fileSize: z.string(),
  scanStatus: z.enum(['pending', 'clean', 'infected', 'failed']),
  uploadedAt: z.string(),
});
export type PrintoutTemplateMedia = z.infer<typeof printoutTemplateMediaSchema>;
export const printoutTemplatePreviewContract = {
  preview: oc
    .route({ method: 'POST', path: '/printout-template-preview' })
    .errors(guarded)
    .input(printoutTemplatePreviewInputSchema)
    .output(z.object({ generatedFileId: z.uuid() })),
  mediaList: oc
    .route({ method: 'GET', path: '/printout-template-media' })
    .errors(guarded)
    .input(paginationInputSchema.extend({ organizationId: z.uuid() }))
    .output(paginated(printoutTemplateMediaSchema)),
  requestMediaUpload: oc
    .route({ method: 'POST', path: '/printout-template-media/upload' })
    .errors(guarded)
    .input(
      z.object({
        organizationId: z.uuid(),
        fileName: z.string().trim().min(1).max(255),
        fileSize: z.number().int().min(1).max(PRINTOUT_TEMPLATE_MEDIA_MAX_BYTES),
      }),
    )
    .output(z.object({ uploadToken: z.string(), uploadUrl: z.string(), expiresAt: z.string() })),
  confirmMediaUpload: oc
    .route({ method: 'POST', path: '/printout-template-media/confirm' })
    .errors(guarded)
    .input(z.object({ uploadToken: z.string().min(1).max(4096) }))
    .output(printoutTemplateMediaSchema),
  mediaDownload: oc
    .route({ method: 'GET', path: '/printout-template-media/{id}/download' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ url: z.string(), expiresInSeconds: z.number().int() })),
};
